import React from "react";
import { czyBladChunku, przeladujPoBledzieChunku, zdejmijStraznikaChunku } from "../utils/bladChunku";
import s from "./ErrorBoundary.module.css";

/**
 * Generic React ErrorBoundary — przechwytuje błędy renderowania w drzewie dzieci,
 * wyświetla przyjazny komunikat zamiast białego ekranu.
 *
 * [zgłoszenie właściciela 2026-09-20] STARA KARTA PO WDROŻENIU. Chunk strony, którego nazwa zmieniła
 * się przy wdrożeniu, kończy się `ChunkLoadError` — dotąd lądował tutaj jako „Wystąpił nieoczekiwany
 * błąd” i wyglądał jak wyrzucenie z aplikacji. Taki błąd NIE jest awarią ekranu: aplikacja się
 * zaktualizowała, a karta ma stary spis plików. Przeładowujemy ją RAZ (utils/bladChunku, strażnik
 * w sessionStorage); gdy po przeładowaniu błąd wraca — to już realna awaria i pokazujemy ekran.
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, przeladowuje: false };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidMount() {
        // Zdrowe zamontowanie = poprzednie przeładowanie po wdrożeniu się udało; następne dostanie swoją szansę.
        zdejmijStraznikaChunku();
    }

    componentDidCatch(error, errorInfo) {
        if (czyBladChunku(error) && przeladujPoBledzieChunku()) {
            this.setState({ przeladowuje: true });
            return;
        }
        // Log crash details to console for debugging
        console.error("[ErrorBoundary] Crash:", error?.message);
        console.error("[ErrorBoundary] Stack:", error?.stack);
        console.error("[ErrorBoundary] Component:", errorInfo?.componentStack);
    }

    /* [A16-15] Kasowanie stanu bledu, gdy zmieni sie `resetKey` (Layout podaje tam adres biezacej
       strony). Warunek `this.state.hasError` jest istotny: dopoki nic sie nie zepsulo, ta metoda nic
       nie robi, wiec zdrowa podstrona NIE jest przemontowywana przy kazdej zmianie adresu. */
    componentDidUpdate(prevProps) {
        if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
            this.setState({ hasError: false, error: null, przeladowuje: false });
        }
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null, przeladowuje: false });
    };

    render() {
        if (this.state.hasError) {
            if (this.state.przeladowuje) {
                return (
                    <div className={s.container} data-testid="aktualizacja-przeladowanie">
                        <div className={s.icon}>&#8635;</div>
                        <h2 className={s.title}>Aplikacja została zaktualizowana</h2>
                        <p className={s.message}>Odświeżam stronę, żeby pobrać nową wersję…</p>
                    </div>
                );
            }

            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className={s.container}>
                    <div className={s.icon}>
                        &#9888;&#65039;
                    </div>
                    <h2 className={s.title}>
                        Wystąpił nieoczekiwany błąd
                    </h2>
                    <p className={s.message}>
                        Przepraszamy za utrudnienia. Spróbuj odświeżyć stronę lub wróć do pulpitu.
                    </p>
                    {/* [A15-09] BYŁO ZLE: sekcja ze szczegółami wyjątku (komunikat + stos wywołań)
                        renderowała się bez żadnego warunku środowiska, mimo etykiety „(dev)".
                        SKUTEK DLA UŻYTKOWNIKA: po awarii ekranu zwykły pracownik dostawał rozwijaną
                        sekcję z treścią wyjątku i stosem wywołań — nazwy plików chunków, a czasem
                        fragment odpowiedzi API. Dla niego to niezrozumiały szum, a przy okazji
                        wyciek szczegółów technicznych na ekran.
                        NAPRAWA: pokazujemy ją tylko poza produkcją. Logowanie w componentDidCatch
                        zostaje bez zmian — jest w konsoli, nie w interfejsie, i pomaga w diagnostyce. */}
                    {process.env.NODE_ENV !== "production" && this.state.error && (
                        <details className={s.details}>
                            <summary>Szczegóły błędu (dev)</summary>
                            <pre className={s.errorText}>
                                {this.state.error.message}
                                {"\n\n"}
                                {this.state.error.stack}
                            </pre>
                        </details>
                    )}
                    <div className={s.actions}>
                        <button
                            onClick={this.handleReset}
                            className={s.retryBtn}
                        >
                            Spróbuj ponownie
                        </button>
                        <button
                            onClick={() => { window.location.href = "/"; }}
                            className={s.homeBtn}
                        >
                            Wróć do pulpitu
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
